import React from 'react';

const LearningPathSection = () => {
  // Base styles for the component
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#0a0a0a',
      padding: '5rem 2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative',
      overflow: 'hidden'
    },
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.02) 1px, transparent 1px),
        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.02) 1px, transparent 1px)
      `,
      backgroundSize: '60px 60px, 80px 80px',
      animation: 'float 20s ease-in-out infinite'
    },
    header: {
      textAlign: 'center',
      marginBottom: '4rem',
      maxWidth: '900px',
      margin: '0 auto 4rem auto'
    },
    title: {
      fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
      fontWeight: '700',
      color: 'white',
      marginBottom: '1.5rem',
      lineHeight: '1.2'
    },
    subtitle: {
      fontSize: 'clamp(1.1rem, 2.5vw, 1.25rem)',
      color: 'rgba(255,255,255,0.7)',
      lineHeight: '1.6',
      fontWeight: '400'
    },
    cardsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    card: {
      borderRadius: '16px',
      padding: '2.5rem',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '1px solid rgba(255,255,255,0.1)'
    },
    cardAdvanced: {
      background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
    },
    cardProfessional: {
      background: 'linear-gradient(135deg, #2d5a3d 0%, #1a3d2e 100%)',
    },
    cardFoundational: {
      background: 'linear-gradient(135deg, #744210 0%, #5a2d0c 100%)',
    },
    iconContainer: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '2rem',
      fontSize: '2rem',
      fontWeight: 'bold',
      color: 'white'
    },
    iconAdvanced: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    iconProfessional: {
      background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
    },
    iconFoundational: {
      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    },
    cardTitle: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: 'white',
      marginBottom: '1rem'
    },
    badge: {
      display: 'inline-block',
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      marginBottom: '1.5rem',
      color: 'white'
    },
    badgeAdvanced: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    badgeProfessional: {
      background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
    },
    badgeFoundational: {
      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    },
    cardDescription: {
      fontSize: '1.1rem',
      color: 'rgba(255,255,255,0.8)',
      lineHeight: '1.6',
      marginBottom: '2rem'
    },
    exploreButton: {
      background: 'rgba(0,0,0,0.3)',
      color: 'white',
      border: 'none',
      padding: '1rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      width: '100%',
      justifyContent: 'center'
    },
    arrow: {
      transition: 'transform 0.3s ease'
    },
    glowEffect: {
      position: 'absolute',
      top: '-50%',
      left: '-50%',
      width: '200%',
      height: '200%',
      background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)',
      opacity: 0,
      transition: 'opacity 0.3s ease'
    }
  };

  const programs = [
    { id: 'advanced', title: 'Advanced Programs', badge: 'Universities & Research', description: 'Advanced research programs, graduate-level AI courses, and faculty development.', icon: '🎓', cardStyle: styles.cardAdvanced, iconStyle: styles.iconAdvanced, badgeStyle: styles.badgeAdvanced },
    { id: 'professional', title: 'Professional Programs', badge: 'Colleges & Industry', description: 'Undergraduate programs, practical AI training, and industry-ready curriculum.', icon: '📋', cardStyle: styles.cardProfessional, iconStyle: styles.iconProfessional, badgeStyle: styles.badgeProfessional },
    { id: 'foundational', title: 'Foundational Programs', badge: 'Schools & Beginners', description: 'Introduction to AI concepts, age-appropriate content, and teacher training.', icon: '🏠', cardStyle: styles.cardFoundational, iconStyle: styles.iconFoundational, badgeStyle: styles.badgeFoundational }
  ];

  return (
    <div style={styles.container} data-component="container">
      {/* Injecting CSS for animations, hover effects, and responsiveness */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          /* Card hover effects */
          [data-component="card"]:hover {
            transform: translateY(-10px);
            box-shadow: 0 20px 40px rgba(0,0,0,0.3);
          }
          [data-component="card"]:hover .glow {
            opacity: 1;
          }

          /* Button hover effects */
          [data-component="explore-button"]:hover {
            background: rgba(255,255,255,0.1);
          }
          [data-component="explore-button"]:hover .arrow {
            transform: translateX(5px);
          }

          /* Responsive styles for tablet and mobile */
          @media (max-width: 800px) {
            [data-component="cards-container"] {
              grid-template-columns: 1fr !important;
              gap: 2.5rem !important;
            }
            [data-component="card"] {
              width: 95% !important;
              margin: 0 auto !important; /* Center the cards */
            }
          }

          @media (max-width: 768px) {
            [data-component="container"] {
              padding: 4rem 1rem !important;
            }
            [data-component="card"] {
              padding: 2rem !important;
            }
          }
        `}
      </style>
      
      <div style={styles.backgroundPattern}></div>
      
      <div style={styles.header}>
        <h1 style={styles.title}>Choose Your Learning Path</h1>
        <p style={styles.subtitle}>
          Our programs are designed to meet learners at every stage of their AI journey, from 
          complete beginners to advanced researchers.
        </p>
      </div>
      
      <div style={styles.cardsContainer} data-component="cards-container">
        {programs.map((program) => (
          <div
            key={program.id}
            style={{...styles.card, ...program.cardStyle}}
            data-component="card"
          >
            <div className="glow" style={styles.glowEffect}></div>
            
            <div style={{...styles.iconContainer, ...program.iconStyle}}>
              {program.icon}
            </div>
            
            <h3 style={styles.cardTitle}>{program.title}</h3>
            
            <div style={{...styles.badge, ...program.badgeStyle}}>
              {program.badge}
            </div>
            
            <p style={styles.cardDescription}>
              {program.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LearningPathSection;